import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, LayoutGrid } from 'lucide-react';
import { SocialIcon } from '../icons/SocialIcon';
import type { SocialPlatform } from '../icons/SocialIcon';

export interface SocialTab {
  id: SocialPlatform | 'summary' | 'all';
  name: string;
}

interface SocialMediaTabsProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
  showSummary?: boolean;
  showAllTab?: boolean;
}

export const SocialMediaTabs: React.FC<SocialMediaTabsProps> = ({
  activeTab,
  onTabChange,
  showSummary = true,
  showAllTab = false
}) => {
  const { t } = useTranslation();

  // Only show supported platforms in tabs (remove Twitter, Google Business, TikTok, Pinterest)
  const platforms: SocialTab[] = [
    { id: 'linkedin' as SocialPlatform, name: 'LinkedIn' },
    { id: 'facebook' as SocialPlatform, name: 'Facebook' },
    { id: 'instagram' as SocialPlatform, name: 'Instagram' },
    { id: 'youtube' as SocialPlatform, name: 'YouTube' },
    { id: 'tiktok' as SocialPlatform, name: 'TikTok' },
    { id: 'twitter' as SocialPlatform, name: 'Twitter' }
  ];

  const allTabs = [
    ...(showAllTab ? [{ id: 'all', name: 'All' }] : []),
    ...(showSummary ? [{ id: 'summary', name: t('analytics.summary') }] : []),
    ...platforms
  ];

  return (
    <div className="mb-6 overflow-x-auto">
      <div className="flex border-b border-gray-200 min-w-max lg:min-w-0">
        {allTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`px-3 lg:px-4 py-2 font-medium text-sm flex items-center gap-1 whitespace-nowrap ${
              activeTab === tab.id
                ? 'text-indigo-600 border-b-2 border-indigo-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.id === 'summary' ? (
              <>
                <Sparkles className="w-4 h-4" />
                <span className="hidden lg:inline">{tab.name}</span>
              </>
            ) : tab.id === 'all' ? (
              <>
                <LayoutGrid className="w-4 h-4" />
                <span className="hidden lg:inline">{tab.name}</span>
              </>
            ) : (
              <>
                <SocialIcon platform={tab.id} size={16} />
                <span className="hidden lg:inline">{tab.name}</span>
              </>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};